import { TableRow } from "../../../components/common/Table/Table";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";

export const parseEntryNames = (entry: any): string[] => {
  if (!entry) return [];
  const rawNames = Array.isArray(entry.name)
    ? entry.name
    : [String(entry.name)];
  const namesFlat: string[] = [];
  rawNames.forEach((n: any) => {
    if (typeof n === "string") {
      n.split(",").forEach((part: string) => {
        const t = part.trim();
        if (t) namesFlat.push(t);
      });
    } else if (n != null) namesFlat.push(String(n));
  });
  return namesFlat;
};

export const buildMailtoHref = (
  addresses?: unknown,
  subject?: string,
): string => {
  const recipients = normalizeEmailAddresses(addresses);
  if (recipients.length === 0) return "";

  // Outlook Classic requires semicolons to resolve multiple mailto recipients.
  const to = recipients
    .map((address) => encodeURIComponent(address).replace(/%40/gi, "@"))
    .join(";");
  const subjectPart = subject ? "?subject=" + encodeURIComponent(subject) : "";
  return "mailto:" + to + subjectPart;
};

const isMailtoAddress = (value: string): boolean => {
  const atIndex = value.indexOf("@");
  return (
    atIndex > 0 &&
    atIndex === value.lastIndexOf("@") &&
    atIndex < value.length - 1 &&
    !/[\s<>]/.test(value) &&
    ![...value].some((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f);
    })
  );
};

/**
 * Normalizes API or UI recipient values into unique, valid mailto addresses.
 * @param values - A single address, an address array, or delimiter-packed values.
 * @returns Unique addresses suitable for a mailto URI.
 */
export const normalizeEmailAddresses = (values: unknown): string[] => {
  const source = Array.isArray(values) ? values : [values];
  const normalized: string[] = [];

  source.forEach((value) => {
    if (typeof value !== "string") return;

    value
      .split(/[;,]/)
      .map((address) => address.trim())
      .filter(isMailtoAddress)
      .forEach((address) => {
        if (!normalized.some((item) => item.toLowerCase() === address.toLowerCase())) {
          normalized.push(address);
        }
      });
  });

  return normalized;
};

export const safeOpenMailClient = (
  addresses?: string[],
  subject?: string,
): boolean => {
  const href = buildMailtoHref(addresses, subject);
  if (!href) return false;
  globalThis.location.href = href;
  return true;
};

export const filterEmails = (addresses?: any[]): any[] =>
  (addresses ?? []).filter(Boolean);

type TeamRoleDropdownConfig = {
  canMemberShipEdit?: boolean;
  loginNuid?: string | null;
  requireAdminForDropdown?: boolean;
};

export const mapRowsWithDropdown = (
  rows: TableRow[],
  isAdmin: boolean | undefined,
  rowKeyPrefix: string,
  roleOptions?: any[],
  onRoleChange?: (row: any, selected: DropdownOption) => void,
  dropdownConfig?: TeamRoleDropdownConfig,
): TableRow[] => {
  return (rows ?? []).map((r, index) => {
    const base = {
      ...r,
      startDate:  r.startdate ?? '',
      privStartDate: r.privlegestartdate ?? '',
      rowKey: r.id ?? r.nuid ?? `${rowKeyPrefix}-${index}`,
      originalData: r,
    } as any;
    const rowNuid = String(r.nuid ?? base.originalData?.nuid ?? "");
    const currentNuid = String(dropdownConfig?.loginNuid ?? "");
    const canShowDropdown =
      Boolean(dropdownConfig?.canMemberShipEdit) &&
      rowNuid !== "" &&
      (rowNuid !== currentNuid ||
        (dropdownConfig?.requireAdminForDropdown === true && Boolean(isAdmin)));

    if (canShowDropdown) {
      const options = (roleOptions ?? []).map((opt: any) => ({
        id: String(opt.roleOrStatus).toLowerCase(),
        label: opt.roleOrStatus,
        value: opt.roleOrStatus,
      }));
      base.teamRole = (
        <div id="teamRole" className="w-1/2!">
          <Dropdown
            selectedValue={
              typeof r.teamRole === "string"
                ? {
                    id: String(r.teamRole).toLowerCase(),
                    label: r.teamRole,
                    value: r.teamRole,
                  }
                : { id: "", label: "", value: "" }
            }
            options={
              options.length > 0
                ? options
                : [{ id: "member", label: "Member", value: "Member" }]
            }
            buttonClassName="min-w-[14rem] min-h-[2.5rem]!"
            menuClassName="min-w-[14rem] max-h-48 overflow-y-auto"
            itemClassName="py-2!"
            onSelect={(selected) => onRoleChange?.(base.originalData, selected)}
          />
        </div>
      );
    }
    return base;
  });
};
